import React from 'react';
import { HighlightTester } from './HighlightTester';
import { CodeRenderer, createSampleWithTemplate } from './CodeRenderer';
import { CodeRendererEnhanced, createEnhancedSample } from './CodeRendererEnhanced';

/**
 * Test Router for Demo Components
 * 
 * Provides a standardized way to test UI demo components and temporary features
 * using test URL parameters.
 * 
 * URL Format: ?test=<component>
 * Examples:
 *   ?test=highlight     - HighlightTester
 *   ?test=coderenderer  - Basic CodeRenderer  
 *   ?test=enhanced      - Enhanced CodeRenderer with validation
 *   ?test=all          - Show all test components
 */

interface TestRouterProps {
  testMode: string;
}

export const TestRouter: React.FC<TestRouterProps> = ({ testMode }) => {
  const renderTestComponent = () => {
    switch (testMode) {
      case 'highlight':
        return (
          <div className="test-container">
            <div className="test-header">
              <h1 className="text-2xl font-bold mb-4">HighlightTester</h1>
              <p className="text-gray-600 mb-6">Visual highlighting test component for SyntaxLens interactions</p>
            </div>
            <HighlightTester />
          </div>
        );

      case 'coderenderer':
        return (
          <div className="test-container">
            <div className="test-header">
              <h1 className="text-2xl font-bold mb-4">CodeRenderer Test</h1>
              <p className="text-gray-600 mb-6">Basic JSON → HTML pipeline for structured lens data</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <CodeRenderer data={createSampleWithTemplate()} />
            </div>
          </div>
        );

      case 'enhanced':
        return (
          <div className="test-container">
            <div className="test-header">
              <h1 className="text-2xl font-bold mb-4">CodeRenderer Enhanced</h1>
              <p className="text-gray-600 mb-6">Enhanced CodeRenderer with validation and confidence scoring</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <CodeRendererEnhanced 
                data={createEnhancedSample()} 
                enableValidation={true}
                enableConfidence={true}
                onValidationError={(errors) => console.log('Validation errors:', errors)}
              />
            </div>
          </div>
        );

      case 'all':
        return (
          <div className="test-container">
            <div className="test-header">
              <h1 className="text-2xl font-bold mb-4">All Test Components</h1>
              <p className="text-gray-600 mb-6">Complete test suite for all demo components</p>
            </div>
            
            <div className="space-y-8">
              {/* HighlightTester */}
              <section className="test-section">
                <h2 className="text-xl font-semibold mb-4 border-b pb-2">HighlightTester</h2>
                <HighlightTester />
              </section>

              {/* CodeRenderer */}
              <section className="test-section">
                <h2 className="text-xl font-semibold mb-4 border-b pb-2">CodeRenderer (Basic)</h2>
                <div className="bg-gray-900 p-6 rounded-lg">
                  <CodeRenderer data={createSampleWithTemplate()} />
                </div>
              </section>

              {/* CodeRenderer Enhanced */}
              <section className="test-section">
                <h2 className="text-xl font-semibold mb-4 border-b pb-2">CodeRenderer (Enhanced)</h2>
                <div className="bg-gray-900 p-6 rounded-lg">
                  <CodeRendererEnhanced 
                    data={createEnhancedSample()} 
                    enableValidation={true}
                    enableConfidence={true}
                    onValidationError={(errors) => console.log('Validation errors:', errors)}
                  />
                </div>
              </section>
            </div>
          </div>
        );

      default:
        return (
          <div className="test-container">
            <div className="test-header">
              <h1 className="text-2xl font-bold mb-4 text-red-600">Unknown Test Mode: {testMode}</h1>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">Available Test URLs:</h2>
                <ul className="space-y-2 text-sm font-mono">
                  <li><code>?test=highlight</code> - HighlightTester component</li>
                  <li><code>?test=coderenderer</code> - Basic CodeRenderer test</li>
                  <li><code>?test=enhanced</code> - Enhanced CodeRenderer with validation</li>
                  <li><code>?test=all</code> - All test components</li>
                </ul>
                
                <h3 className="text-md font-semibold mt-4 mb-2">Keyboard Shortcuts:</h3>
                <ul className="space-y-1 text-sm">
                  <li><kbd className="px-2 py-1 bg-gray-200 rounded">Ctrl+Shift+T</kbd> - Toggle test mode</li>
                  <li><kbd className="px-2 py-1 bg-gray-200 rounded">Ctrl+Shift+H</kbd> - Quick HighlightTester</li>
                </ul>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Test Navigation */}
      <nav className="bg-gray-100 border-b border-gray-200 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold">SyntaxLens Test Suite</h1>
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
              Mode: {testMode}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <a href="?test=highlight" className="text-blue-600 hover:underline">Highlight</a>
            <a href="?test=coderenderer" className="text-blue-600 hover:underline">CodeRenderer</a>
            <a href="?test=enhanced" className="text-blue-600 hover:underline">Enhanced</a>
            <a href="?test=all" className="text-blue-600 hover:underline">All</a>
            <a href="/" className="text-gray-600 hover:underline">← Back to App</a>
          </div>
        </div>
      </nav>

      {/* Test Content */}
      <main className="max-w-6xl mx-auto p-6">
        {renderTestComponent()}
      </main>

      <style jsx>{`
        .test-container {
          max-width: none;
        }
        
        .test-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .test-section {
          padding: 1.5rem 0;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .test-section:last-child {
          border-bottom: none;
        }
        
        code {
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        
        kbd {
          display: inline-block;
          font-family: monospace;
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
};

export default TestRouter;